// Storage manager utility for handling localStorage issues
export class StorageManager {
  private static readonly TOKEN_KEY = 'token';
  private static readonly STORAGE_TEST_KEY = 'storage_test';

  // Test if localStorage is available and working
  static isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Get token with error handling
  static getToken(): string | null {
    try {
      if (!this.isStorageAvailable()) {
        console.warn('localStorage is not available');
        return null;
      }
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  }

  // Set token with error handling
  static setToken(token: string): boolean {
    try {
      if (!this.isStorageAvailable()) {
        console.warn('localStorage is not available');
        return false;
      }
      localStorage.setItem(this.TOKEN_KEY, token);
      
      // Verify the token was stored correctly
      const storedToken = localStorage.getItem(this.TOKEN_KEY);
      if (storedToken !== token) {
        console.error('Token storage verification failed');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error setting token in localStorage:', error);
      return false;
    }
  }

  // Remove token with error handling
  static removeToken(): boolean {
    try {
      if (!this.isStorageAvailable()) {
        return true; // Consider it "removed" if storage isn't available
      }
      localStorage.removeItem(this.TOKEN_KEY);
      return true;
    } catch (error) {
      console.error('Error removing token from localStorage:', error);
      return false;
    }
  }

  // Check if token exists and is valid
  static hasValidToken(): boolean {
    const token = this.getToken();
    return token !== null && token.length > 0;
  }

  // Get all localStorage keys for debugging
  static getAllKeys(): string[] {
    try {
      if (!this.isStorageAvailable()) {
        return [];
      }
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Error getting localStorage keys:', error);
      return [];
    }
  }

  // Clear all app-related data
  static clearAppData(): boolean {
    try {
      if (!this.isStorageAvailable()) {
        return true;
      }
      localStorage.removeItem(this.TOKEN_KEY);
      // Add other app-specific keys here if needed
      return true;
    } catch (error) {
      console.error('Error clearing app data:', error);
      return false;
    }
  }

  // Monitor localStorage for changes (useful for debugging)
  static monitorStorage(callback: (event: StorageEvent) => void): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', callback);
    }
  }

  // Stop monitoring localStorage
  static stopMonitoringStorage(callback: (event: StorageEvent) => void): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', callback);
    }
  }

  // Test storage functionality
  static testStorage(): { available: boolean; writable: boolean; readable: boolean } {
    const result = {
      available: this.isStorageAvailable(),
      writable: false,
      readable: false
    };

    if (!result.available) {
      return result;
    }

    try {
      // Test write
      localStorage.setItem(this.STORAGE_TEST_KEY, 'test_value');
      result.writable = true;

      // Test read
      const readValue = localStorage.getItem(this.STORAGE_TEST_KEY);
      result.readable = readValue === 'test_value';

      // Cleanup
      localStorage.removeItem(this.STORAGE_TEST_KEY);
    } catch (error) {
      console.error('Storage test failed:', error);
    }

    return result;
  }
}
